// src/features/settings/components/registration-codes-panel/RegistrationCodesPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useRegistrationCodes } from '../../../tidsregistering/hooks/useRegistrationCodes';
import { useTidsregistreringRegistrationCodePermissions } from '../../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../../hooks/mui/useConfirmDialog';
import { useAuth } from '../../../../context/AuthContext';
import RegistrationCodeCreateForm from './RegistrationCodeCreateForm';
import RegistrationCodeEditDialog from './RegistrationCodeEditDialog';
import RegistrationCodesGrid from './RegistrationCodesGrid';

const EMPTY_FORM = {
  code: '',
  title: '',
  description: '',
  reason_code_type_id: '',
  registration_type: 'plannedAttendance',
  work_time_type: 'work',
  registration_category: 'normal_hours',
  statistical_category: 'attendance',
  account_handling: 'none',
  time_off_type: 'none',
  leave_type: 'none',
  obscured_category: null,
  funding_source: null,
  is_registration_code: true,
  is_time_detail_code: true,
  is_wishable: false,
  is_allowed_without_end_date: false,
  is_end_day_code: false,
  is_allowed_to_delete_activities: false,
  requires_documentation: false,
  required_seniority_months: 0,
  max_per_period: null,
  period_type: null,
};

export default function RegistrationCodesPanel() {
  const { t } = useTranslation(['settings', 'common']);
  const { user } = useAuth();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { canCreate, canUpdate, canDelete } = useTidsregistreringRegistrationCodePermissions();

  const {
    registrationCodes,
    reasonCodeTypes,
    loading,
    error,
    clearError,
    fetchForDepartment,
    fetchReasonCodeTypes,
    createRegistrationCode,
    updateRegistrationCode,
    deleteRegistrationCode,
    seedDefaultCodes,
  } = useRegistrationCodes();

  const [addFormVisible, setAddFormVisible] = useState(false);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [editRow, setEditRow] = useState(null);

  useEffect(() => {
    fetchForDepartment();
    fetchReasonCodeTypes();
  }, [fetchForDepartment, fetchReasonCodeTypes]);

  const handleFormChange = useCallback((field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: null }));
  }, []);

  const handleCreate = async () => {
    const errors = {};
    if (!formValues.code.trim()) errors.code = t('settings:registration_codes.validation.code_required');
    if (!formValues.title.trim()) errors.title = t('settings:registration_codes.validation.title_required');
    if (!formValues.reason_code_type_id) errors.reason_code_type_id = t('settings:registration_codes.validation.reason_code_type_required');

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const result = await createRegistrationCode(formValues);
    if (result.success) {
      showSuccess(t('settings:registration_codes.success.created'));
      setFormValues(EMPTY_FORM);
      setAddFormVisible(false);
      fetchForDepartment();
    } else {
      showError(result.error);
    }
  };

  const handleCancelCreate = useCallback(() => {
    setFormValues(EMPTY_FORM);
    setFormErrors({});
    setAddFormVisible(false);
  }, []);

  const handleDelete = useCallback((row) => {
    showConfirmDialog({
      title: t('settings:registration_codes.confirm.delete_title'),
      message: t('settings:registration_codes.confirm.delete_message', { title: row.title, code: row.code }),
      severity: 'error',
      onConfirm: async () => {
        const result = await deleteRegistrationCode(row.registration_code_id);
        if (result.success) {
          showSuccess(t('settings:registration_codes.success.deleted'));
          fetchForDepartment();
        } else {
          showError(result.error);
        }
      },
    });
  }, [showConfirmDialog, deleteRegistrationCode, fetchForDepartment, showSuccess, showError, t]);

  const handleEdit = useCallback((row) => {
    setEditRow(row);
  }, []);

  const handleEditClose = useCallback(() => {
    setEditRow(null);
  }, []);

  const handleEditSubmit = useCallback(async (data) => {
    const result = await updateRegistrationCode(editRow.registration_code_id, data);
    if (result.success) {
      showSuccess(t('settings:registration_codes.success.updated'));
      setEditRow(null);
      fetchForDepartment();
    } else {
      showError(result.error);
    }
  }, [editRow, updateRegistrationCode, showSuccess, showError, fetchForDepartment, t]);

  const handleSeed = useCallback(() => {
    showConfirmDialog({
      title: t('settings:registration_codes.confirm.seed_title'),
      message: t('settings:registration_codes.confirm.seed_message'),
      onConfirm: async () => {
        const result = await seedDefaultCodes();
        if (result.success) {
          showSuccess(t('settings:registration_codes.success.seeded'));
          fetchForDepartment();
        } else {
          showError(result.error);
        }
      },
    });
  }, [showConfirmDialog, seedDefaultCodes, fetchForDepartment, showSuccess, showError, t]);

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('settings:registration_codes.description')}
        </Typography>

        <Box mb={2} display="flex" gap={1}>
          {canCreate && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddFormVisible(true)}
                disabled={addFormVisible}
              >
                {t('settings:registration_codes.buttons.add')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleSeed}
                disabled={loading}
              >
                {t('settings:registration_codes.buttons.seed')}
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchForDepartment()}
          >
            {t('common:buttons.refresh')}
          </Button>
        </Box>

        <RegistrationCodeCreateForm
          visible={addFormVisible}
          values={formValues}
          errors={formErrors}
          loading={loading}
          reasonCodeTypes={reasonCodeTypes}
          onChange={handleFormChange}
          onSubmit={handleCreate}
          onCancel={handleCancelCreate}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <RegistrationCodesGrid
          registrationCodes={registrationCodes}
          loading={loading}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Paper>

      <RegistrationCodeEditDialog
        open={Boolean(editRow)}
        row={editRow}
        loading={loading}
        onClose={handleEditClose}
        onSubmit={handleEditSubmit}
      />

      <ConfirmDialog />
    </Box>
  );
}
